import {
    IonAlert,
    IonIcon,
    IonItem,
    IonItemDivider,
    IonItemGroup,
    IonLabel,
    IonList,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { bluetoothOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import { ESPProvisionManager } from '../../../provisioning/ESPProvisionManager';
import {
    meshCompletedAtom,
    roomsWithDevicesAndStateAtom,
    updateDBStateAtom,
} from '../../../store/Atoms';
import { trackActionsRequest } from '../../../store/Requests';
import { THomeAssetData } from '../../../types/HomeAssetData';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';

enum LoadingType {
    ProvisioningMacs = 'ProvisioningMacs',
    EndProvisioning = 'EndProvisioning',
}

const MAX_MACS_PER_CHUNK = 5;

const ProvisioningMacs: React.FC<IInstallationRouteProps> = ({ match }) => {
    const provisionManager = ESPProvisionManager.getInstance();
    const router = useCustomRouter();
    const jobId = +match.params.jobId;
    const [presentToast] = useIonToast();
    const [value] = useAtom(roomsWithDevicesAndStateAtom);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [loadingType, setLoadingType] = useState<LoadingType | undefined>(
        undefined
    );
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const [, setMeshCompleted] = useAtom(meshCompletedAtom);
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data : []),
        [value]
    );
    const connectedMacAddress = provisionManager
        .getEspDevice()
        .getDeviceMacAddress();

    const onProvisioningMacsHandler = useCallback(async () => {
        setLoadingType(LoadingType.ProvisioningMacs);
        try {
            const macAddresses: string[] = [];
            rooms.forEach((room) => {
                room.devices.forEach((device) => {
                    const mac = device.macAddress;
                    if (mac !== connectedMacAddress) {
                        // The ESP device accepts 12 character macs
                        macAddresses.push(
                            mac.length === 16 ? mac.substring(4) : mac
                        );
                    }
                });
            });
            // Split macs into chunks of MAX_MACS_PER_CHUNK to prevent the ESP device from crashing
            const macAddressesChunks = [];
            while (macAddresses.length > 0) {
                const chunk = macAddresses.splice(0, MAX_MACS_PER_CHUNK);
                macAddressesChunks.push(chunk);
            }
            for (const chunk of macAddressesChunks) {
                const failedMacs = await provisionManager
                    .getEspDevice()
                    .provisionMacs(chunk);
                trackActionsRequest('macs_provisioned', {
                    macs: chunk,
                    macsFailed: failedMacs,
                });
            }

            setIsAlertOpen(true);
        } catch (e) {
            const error = e as Error;
            presentToast({
                message: error.message,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoadingType(undefined);
    }, [connectedMacAddress, presentToast, provisionManager, rooms]);

    const onEndProvisioningHandler = useCallback(async () => {
        setLoadingType(LoadingType.EndProvisioning);
        try {
            await provisionManager.getEspDevice().endMacProvisioning();
            await provisionManager.getEspDevice().disconnectDevice();
            setMeshCompleted(true);
            await updateDBState();
            trackActionsRequest('end_provisioning');
            router.push(`/${jobId}/installation/mesh/status`, 'forward');
        } catch (e) {
            const error = e as Error;
            presentToast({
                message: error.message,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoadingType(undefined);
    }, [
        jobId,
        presentToast,
        provisionManager,
        router,
        setMeshCompleted,
        updateDBState,
    ]);

    return (
        <InterventionPage title={t`Mesh`}>
            <div className="ion-padding">
                <LoadingButton
                    disabled={loadingType === LoadingType.EndProvisioning}
                    loading={loadingType === LoadingType.ProvisioningMacs}
                    onClick={onProvisioningMacsHandler}
                    expand="block"
                >
                    <Trans>Propagate to Macs</Trans>
                </LoadingButton>
            </div>
            <div className="ion-padding-start ion-padding-end ion-padding-bottom">
                <LoadingButton
                    disabled={loadingType === LoadingType.ProvisioningMacs}
                    loading={loadingType === LoadingType.EndProvisioning}
                    onClick={onEndProvisioningHandler}
                    expand="block"
                >
                    <Trans>End Provisioning</Trans>
                </LoadingButton>
            </div>
            <IonList>
                {rooms.map((room) => (
                    <IonItemGroup key={room.roomId}>
                        <IonItemDivider color="primary">
                            <IonLabel>{room.name}</IonLabel>
                        </IonItemDivider>
                        {room.devices.map((device: THomeAssetData) => {
                            return (
                                <IonItem key={device.macAddress}>
                                    {device.macAddress ===
                                    connectedMacAddress ? (
                                        <IonIcon
                                            slot="end"
                                            icon={bluetoothOutline}
                                        />
                                    ) : null}
                                    <IonLabel>{device.macAddress}</IonLabel>
                                </IonItem>
                            );
                        })}
                    </IonItemGroup>
                ))}
            </IonList>
            <IonAlert
                isOpen={isAlertOpen}
                onDidDismiss={() => setIsAlertOpen(false)}
                header={t`Propagation to macs finished!`}
                buttons={[
                    {
                        text: t`Ok`,
                        id: 'confirm-button',
                    },
                ]}
            />
        </InterventionPage>
    );
};

export default ProvisioningMacs;
